import React, { memo, useState } from "react";

import { Box } from "@UI/Components/Box/Box";

/**
 * Figma specifies no duration or easing for the fill animation — this is a
 * restrained default, not a measured value.
 */
const FILL_TRANSITION_MS = 250;

const prefersReducedMotion = (): boolean => {
  if (typeof window === "undefined") return false;
  if (typeof window.matchMedia !== "function") return false;

  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
};

export interface ITaskCreationProgressBarProps {
  /** Required questions answered so far, excluding the description screen. */
  completed?: number;
  /** Total required questions in the flow, excluding the description screen. */
  total?: number;
}

/**
 * The flow's progress bar (RT-4321): a flat, solid fill — deliberately not the
 * description field's pink/orange gradient. Renders nothing without a `total`,
 * which covers the description screen (no progress bar at all).
 */
export const TaskCreationProgressBar = memo(
  ({ completed = 0, total }: ITaskCreationProgressBarProps) => {
    const [reducedMotion] = useState(prefersReducedMotion);

    if (!total) return null;

    const ratio = Math.min(Math.max(completed / total, 0), 1);

    return (
      <Box
        role="progressbar"
        aria-valuenow={completed}
        aria-valuemin={0}
        aria-valuemax={total}
        height="4px"
        width="100%"
        background="seaGreen.100"
        borderRadius="999px"
        overflow="hidden"
      >
        <Box
          height="100%"
          width={`${ratio * 100}%`}
          background="seaGreen.500"
          borderRadius="999px"
          transition={
            reducedMotion ? "none" : `width ${FILL_TRANSITION_MS}ms ease`
          }
        />
      </Box>
    );
  }
);

TaskCreationProgressBar.displayName = "TaskCreationProgressBar";
